/**
 * Main schedule body: left rail (projects / groups) + scroll-synced timeline wrapped in DnD.
 * Keeps vertical scroll between the panel and grid aligned, wires drag overlay from ScopeBar
 * drag start, and lets child timeline layers pull their own ScheduleContext actions.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SCHEDULE_LEFT_PANEL_WIDTH } from "./constants";
import ScheduleTimelineDnd from "./dnd/ScheduleTimelineDnd";
import ScheduleLoadingOverlay from "./ScheduleLoadingOverlay";
import { Spinner } from "../../../components/ui";
import ScheduleProjectsPanel from "./ScheduleProjectsPanel";
import TimelineCanvas from "./timeline/TimelineCanvas";
import { ConfirmModal } from "../../../components/ui";
import { useScheduleContext } from "../../../context/domain/planning/ScheduleContext";
import { BG_SURFACE, SCHEDULE_BAR_STYLE } from "./style";

const SCHEDULE_LOAD_TRANSITION_MS = 1000;

/**
 * Schedule grid shell. Reads schedule state from context so route/page components
 * do not have to pass the whole timeline API through multiple render layers.
 *
 * @param {void}
 * @returns {JSX.Element}
 */
export default function ScheduleTimelineBody() {
  const {
    scheduleRows,
    scopeBars,
    rowHeightsById,
    expandedProjectIds,
    expandedGroupIds,
    toggleProject,
    toggleGroup,
    setHideEmptyProjects,
    projectSearchQuery,
    openCreateGroup,
    removeScheduleGroup,
    renameScheduleGroup,
    handleBlockDragOver,
    handleBlockDragEnd,
    handleBlockDragCancel,
    horizontalScrollLeft,
    setHorizontalScrollLeft,
    isScheduleTimelineLoading,
    isScheduleWindowLoading,
    readOnly,
  } = useScheduleContext();
  const leftScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const syncLock = useRef(false);
  const [dragOverlayScopeBar, setDragOverlayScopeBar] = useState(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [loadingOverlayVisible, setLoadingOverlayVisible] = useState(true);
  const [deleteGroupId, setDeleteGroupId] = useState(null);

  useEffect(() => {
    if (isScheduleTimelineLoading) {
      setShowLoadingOverlay(true);
      setLoadingOverlayVisible(true);
      return undefined;
    }

    const successHoldMs = Math.round(SCHEDULE_LOAD_TRANSITION_MS * 0.4);
    const fadeTimer = window.setTimeout(() => {
      setLoadingOverlayVisible(false);
    }, successHoldMs);
    const hideTimer = window.setTimeout(() => {
      setShowLoadingOverlay(false);
    }, successHoldMs + SCHEDULE_LOAD_TRANSITION_MS);

    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(hideTimer);
    };
  }, [isScheduleTimelineLoading]);

  /**
   * Mirror vertical scroll between the left rail and grid without a feedback loop.
   *
   * @param {number} scrollTop Source container scrollTop.
   * @param {"left"|"right"} source Which pane initiated the scroll.
   * @returns {void}
   */
  const syncVerticalScroll = useCallback((scrollTop, source) => {
    const left = leftScrollRef.current;
    const right = rightScrollRef.current;
    const target = source === "left" ? right : left;
    if (!target || syncLock.current) {
      return;
    }
    if (Math.abs(target.scrollTop - scrollTop) < 1) {
      return;
    }
    syncLock.current = true;
    target.scrollTop = scrollTop;
    window.requestAnimationFrame(() => {
      syncLock.current = false;
    });
  }, []);

  /**
   * Keep the timeline grid vertically aligned with the projects panel.
   *
   * @param {import('react').UIEvent<HTMLElement>} e Left-rail scroll event.
   * @returns {void}
   */
  const handleLeftScroll = useCallback(
    (e) => {
      syncVerticalScroll(e.currentTarget.scrollTop, "left");
    },
    [syncVerticalScroll]
  );

  /**
   * Sync vertical rail scroll and publish horizontal offset to the header strip.
   *
   * @param {import('react').UIEvent<HTMLElement>} e Timeline scroll event.
   * @returns {void}
   */
  const handleRightScroll = useCallback(
    (e) => {
      syncVerticalScroll(e.currentTarget.scrollTop, "right");
      setHorizontalScrollLeft(e.currentTarget.scrollLeft);
    },
    [syncVerticalScroll, setHorizontalScrollLeft]
  );

  /**
   * Compact label chip shown under the pointer while a ScopeBar is being dragged.
   *
   * @param {void}
   * @returns {JSX.Element|null}
   */
  const dragOverlay = useMemo(() => {
    if (!dragOverlayScopeBar) {
      return null;
    }
    return (
      <div
        className="flex max-w-[min(100vw,480px)] items-center truncate rounded px-2 py-1 text-xs font-medium shadow-lg"
        style={{
          backgroundColor:
            dragOverlayScopeBar.color ?? SCHEDULE_BAR_STYLE.defaultColor,
          color: SCHEDULE_BAR_STYLE.textColor,
          minWidth: 120,
        }}
      >
        {dragOverlayScopeBar.label}
      </div>
    );
  }, [dragOverlayScopeBar]);

  /**
   * Capture the dragged bar so the overlay can render its label/color.
   *
   * @param {Object} event dnd-kit drag-start event.
   * @returns {void}
   */
  const handleDndDragStart = useCallback(
    (event) => {
      const scopeBarId = event.active.data.current?.barId;
      const matchedScopeBar = scopeBars.find((x) => x.id === scopeBarId);
      setDragOverlayScopeBar(matchedScopeBar ?? null);
    },
    [scopeBars]
  );

  /**
   * Persist the drop via block actions and clear the drag overlay.
   *
   * @param {Object} event dnd-kit drag-end event.
   * @returns {void}
   */
  const handleDndDragEnd = useCallback(
    (event) => {
      handleBlockDragEnd(event);
      setDragOverlayScopeBar(null);
    },
    [handleBlockDragEnd]
  );

  /**
   * Cancel an in-progress bar drag and hide the overlay.
   *
   * @param {void}
   * @returns {void}
   */
  const handleDndDragCancel = useCallback(() => {
    handleBlockDragCancel();
    setDragOverlayScopeBar(null);
  }, [handleBlockDragCancel]);

  /**
   * Confirm group deletion before moving its blocks back to the project row.
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const confirmDeleteGroup = useCallback(async () => {
    const groupId = deleteGroupId;
    setDeleteGroupId(null);
    if (groupId == null) {
      return;
    }
    await removeScheduleGroup(groupId);
  }, [deleteGroupId, removeScheduleGroup]);

  return (
    <div className={`relative flex min-h-[260px] ${BG_SURFACE}`}>
      <div
        className={`relative shrink-0 bg-white transition-opacity duration-500 ${
          isScheduleTimelineLoading ? "opacity-0" : "opacity-100"
        }`}
        style={{
          width: SCHEDULE_LEFT_PANEL_WIDTH,
          transitionDuration: `${SCHEDULE_LOAD_TRANSITION_MS}ms`,
        }}
      >
        <ScheduleProjectsPanel
          width={SCHEDULE_LEFT_PANEL_WIDTH}
          scheduleRows={scheduleRows}
          rowHeightsById={rowHeightsById}
          expandedProjectIds={expandedProjectIds}
          expandedGroupIds={expandedGroupIds}
          onToggleProject={toggleProject}
          onToggleGroup={toggleGroup}
          onAddGroup={openCreateGroup}
          onDeleteGroup={setDeleteGroupId}
          onRenameGroup={renameScheduleGroup}
          onShowAllProjects={() => setHideEmptyProjects(false)}
          projectSearchQuery={projectSearchQuery}
          readOnly={readOnly}
          scrollContainerRef={leftScrollRef}
          onScroll={handleLeftScroll}
        />
      </div>
      {readOnly ? (
        <div
          className={`relative z-[1] flex min-h-[260px] flex-1 transition-opacity duration-500 ${
            isScheduleTimelineLoading ? "opacity-0" : "opacity-100"
          }`}
          style={{ transitionDuration: `${SCHEDULE_LOAD_TRANSITION_MS}ms` }}
        >
          <TimelineCanvas
            horizontalScrollLeft={horizontalScrollLeft}
            scrollContainerRef={rightScrollRef}
            onScroll={handleRightScroll}
          />
        </div>
      ) : (
        <ScheduleTimelineDnd
          overlay={dragOverlay}
          onDragStart={handleDndDragStart}
          onDragOver={handleBlockDragOver}
          onDragEnd={handleDndDragEnd}
          onDragCancel={handleDndDragCancel}
        >
          <div
            className={`relative z-[1] flex min-h-[260px] flex-1 transition-opacity duration-500 ${
              isScheduleTimelineLoading ? "opacity-0" : "opacity-100"
            }`}
            style={{ transitionDuration: `${SCHEDULE_LOAD_TRANSITION_MS}ms` }}
          >
            <TimelineCanvas
              horizontalScrollLeft={horizontalScrollLeft}
              scrollContainerRef={rightScrollRef}
              onScroll={handleRightScroll}
            />
          </div>
        </ScheduleTimelineDnd>
      )}
      {showLoadingOverlay ? (
        <ScheduleLoadingOverlay
          visible={loadingOverlayVisible}
          complete={!isScheduleTimelineLoading}
          durationMs={SCHEDULE_LOAD_TRANSITION_MS}
        />
      ) : null}
      {isScheduleWindowLoading && !isScheduleTimelineLoading ? (
        <div
          className="pointer-events-none absolute right-3 top-3 z-[25] flex items-center gap-2 rounded-md border border-gray-200 bg-white/95 px-2 py-1 shadow-sm"
          aria-live="polite"
          aria-busy="true"
        >
          <Spinner className="text-primary h-4 w-4" variant="oval" color="currentColor" />
          <span className="text-xs text-gray-600">Loading timeline data…</span>
        </div>
      ) : null}
      <ConfirmModal
        isOpen={deleteGroupId != null}
        title="Delete Group?"
        text="This will delete the group and move all scope blocks into the project row"
        buttonText="Confirm"
        onConfirm={confirmDeleteGroup}
        onClose={() => setDeleteGroupId(null)}
      />
    </div>
  );
}
